import { useState, useEffect } from "react";
import { subscribers as api, type SentEmailListItem, type SentEmail } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { X, Mail, ArrowLeft, AlertCircle, Inbox } from "lucide-react";

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function SubscriberEmailsDialog({
  subscriberId,
  subscriberEmail,
  onClose,
}: {
  subscriberId: string;
  subscriberEmail: string;
  onClose: () => void;
}) {
  const [emails, setEmails] = useState<SentEmailListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SentEmail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.listEmails(subscriberId)
      .then((res) => { if (!cancelled) setEmails(res.emails); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load emails"); });
    return () => { cancelled = true; };
  }, [subscriberId]);

  const openEmail = async (id: string) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const email = await api.getEmail(subscriberId, id);
      setSelected(email);
    } catch (e: any) {
      setError(e?.message || "Failed to load email");
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            {selected && (
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900">
                <ArrowLeft size={18} />
              </button>
            )}
            <Mail size={18} className="text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">
              {selected ? "Email details" : "Sent emails"}
            </h2>
            <span className="text-sm text-slate-500">· {subscriberEmail}</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {error && (
            <div className="m-6 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {!selected && !emails && !error && (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          )}

          {!selected && emails && emails.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Inbox size={32} className="mb-2 opacity-50" />
              <p className="text-sm">No emails sent to this subscriber yet</p>
            </div>
          )}

          {!selected && emails && emails.length > 0 && (
            <div className="divide-y divide-slate-100">
              {emails.map((e) => (
                <button
                  key={e.id}
                  onClick={() => openEmail(e.id)}
                  disabled={loadingDetail}
                  className="w-full text-left px-6 py-3 hover:bg-slate-50 transition disabled:opacity-50"
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{e.subject || "(no subject)"}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{e.campaign_name || "—"}</div>
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">{formatRelative(e.sent_at)}</div>
                  </div>
                </button>
              ))}
              {emails.length >= 200 && (
                <div className="px-6 py-3 text-xs text-slate-400 text-center">
                  Showing most recent 200 emails
                </div>
              )}
            </div>
          )}

          {selected && (
            <div className="flex flex-col">
              <dl className="px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                <dt className="text-slate-500">To</dt><dd className="text-slate-900 font-mono">{selected.to_email}</dd>
                <dt className="text-slate-500">Subject</dt><dd className="text-slate-900 font-medium">{selected.subject}</dd>
                <dt className="text-slate-500">Campaign</dt><dd className="text-slate-900">{selected.campaign_name || "—"}</dd>
                <dt className="text-slate-500">Sent</dt><dd className="text-slate-900">{new Date(selected.sent_at).toLocaleString()}</dd>
                <dt className="text-slate-500">Message ID</dt><dd className="text-slate-500 font-mono truncate">{selected.message_id || "—"}</dd>
              </dl>
              <iframe
                sandbox=""
                srcDoc={selected.html_content}
                title="Email content"
                className="w-full bg-white"
                style={{ height: "60vh", border: "none" }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
